// UI 貼圖載入 — 沿用 Unity Art/Game/、Art/Symbol/ 與 SlotFramework/Art/UI/ 資源（見 docs/UNITY_SOURCE_ANALYSIS.md）。
// 這些圖是移植時直接從 Unity 來源複製到 public/images/ui/（複製清單見 CLAUDE.md 目前狀態），
// 檔名沿用來源語意，key 則改為 PixiJS 端可讀的語意名稱。
//
// 對照截圖各 UI 區塊的來源檔：
// - logo：右上「小紅帽」金框標題（Slot026 Art/Game/zh-Hant/logo.png）
// - character：右側角色立繪（Slot026 Art/Bg/bg_loadingCharacter.png，Unity 實機為 Spine ani_character，
//   本移植先用載入頁靜態立繪替代，見 CLAUDE.md 簡化說明）
// - waysBoard：盤面左上「組合方式」紅色木牌（Slot026 Art/Game/pic_mageways.png）
// - woodCell1~4：符號格木紋底（Slot026 Art/Symbol/woodBg1~4.png）。已核對 SymbolData.asset：這是「符號階級框」，
//   非「大符號高度」——LA/LK/LQ/LJ→woodBg1、H7→woodBg2、H6→woodBg3、H5→woodBg4；可長條化的 6 個符號無框。
// - woodBeam / woodBeamGold：頂部插槽木橫條（Slot026 Art/Game/pic_wood.png / pic_woodGlod.png）
// - stageBg：盤面輪軸凹槽背景（Slot026 Art/Game/pic_stageBg.png，744×976，左右鏡像兩半，墊在轉輪下方）
// - stage：盤面裝飾邊框（Slot026 Art/Game/pic_stage.png，744×976，左右鏡像兩半，蓋在轉輪上方為前景框）
// - buyBtnBg / buyBtnLabel：Buy Bonus 主按鈕的 Unity 真實素材——藍紫霓虹底圖 pic_buy 疊白描邊字圖
//   tx_btnBuyBonus（SlotFramework Art/UI/BottomBar/pic_buy.png ＋ LocalizeSprite/zh_TW/tx_btnBuyBonus.png）。
// - popupBg / titleBg / numBg / piso：Buy Bonus 彈窗（Unity BuyBonus.prefab）的面板底、標題條、
//   金額底框、₱ 貨幣符號（SlotFramework Art/UI/BottomBar/pic_popupBg・pic_numBg・pic_piso、Local/Loading/pic_titleBg）。
// - popupBg2 / btnBuy1：Buy Bonus Step1 選項卡的卡片底（430×500）與 BUY 鈕（440×119）
//   （SlotFramework Art/UI/BottomBar/pic_popupBg2・btn_BUY1）。
// - btnCircle / btnClose：Buy Bonus 右上關閉鈕的圓底（157×157）與 X 圖（84×84）
//   （SlotFramework Art/UI/BottomBar/btn_circle、Local/Loading/btn_close）。
// - buyScatter：Buy Bonus 選項卡/確認頁的「三張 Scatter 警示牌」內容疊圖（Slot026 Art/Symbol/SS.png
//   214×192；CommonPanel.prefab 對 BuyBonus 的 6 處 m_Sprite 覆寫皆指向此圖，兩步各疊三張）。
// - btnStart / btnCancel / txStart / txCancel：確認彈窗的確認鈕（綠膠囊底＋「開始」字）與取消鈕（紅膠囊底＋「取消」字）
//   （SlotFramework Local/Loading/btn_start・btn_cancel ＋ LocalizeSprite/zh_TW/tx_start・tx_cancel）。
// - iconMenu/iconInfo/iconWallet/iconBet/iconAuto(+On)/iconTurbo(+On)：底部列線描圖示，皆為 Unity BottomBar.prefab
//   實際綁定的檔（btn_set／btn_info／btn_wallet／btn_bet_／btn_auto(+_on)／btn_fast_normal・btn_fast_quick）。
//   Turbo／自動的開關態在 Unity 是換圖（BottomBarView.cs turboOffSprite/turboQuickSprite）而非著色。
// - iconPlus/iconMinus：押注 ±（Unity 只用於 BuyBonus.prefab 的 Step1 押注調整；底部列本身無 ± 鈕）
// - barBg / barBgPortrait：底部列背景漸層（SlotFramework Art/UI/BottomBar/pic_under_land.png 40×162、
//   pic_under_por.png 30×308；橫版滿版寬釘底、Image Type=Simple 直接橫向拉伸）。
//   ⚠️ Slot026 Art/Game/pic_barBg.png 並非底部列背景（只被 Slot026_LoadingUI.prefab 使用），先前誤用已修正。
// - marqueeBg / winBandBg：中央跑馬燈／贏分帶的底板（pic_marquee_1 深棕漸層 318×144、pic_marquee_2 粉紅漸層
//   318×144，皆 Simple 拉伸；Unity 贏分帶為 marquee_1 700×144 上疊 marquee_2 695.5×145，跑馬燈為 marquee_1 650×144）
// - infinitas：自動遊戲「無限次」符號（SlotFramework infinitas.png 138×178，SPIN 鈕中央）
// - spinButton：SPIN 圓鈕（SlotFramework btn_spin_up.png，已核對 prefab 引用鏈確認為本作實際使用）
// - frameTrim：盤面外框木紋長條（Slot026 Art/Game/pic_pillar.png）

import { type Texture } from 'pixi.js'
import { ensureAtlases, loadTexture } from '../../framework/assets/spriteAtlas'
import { getLocale, type Locale } from '../../framework/i18n/i18n'

const URLS = {
  logo: '/images/ui/logo.webp',
  character: '/images/ui/character.webp',
  waysBoard: '/images/ui/pic_mageways.webp',
  woodCell1: '/images/ui/woodBg1.webp',
  woodCell2: '/images/ui/woodBg2.webp',
  woodCell3: '/images/ui/woodBg3.webp',
  woodCell4: '/images/ui/woodBg4.webp',
  woodBeam: '/images/ui/pic_wood.webp',
  woodBeamGold: '/images/ui/pic_woodGlod.webp',
  buyBtnBg: '/images/ui/pic_buy.webp',
  buyBtnLabel: '/images/ui/tx_btnBuyBonus.webp',
  popupBg: '/images/ui/pic_popupBg.webp',
  popupBg2: '/images/ui/pic_popupBg2.webp',
  titleBg: '/images/ui/pic_titleBg.webp',
  numBg: '/images/ui/pic_numBg.webp',
  piso: '/images/ui/pic_piso.webp',
  btnBuy1: '/images/ui/btn_BUY1.webp',
  btnCircle: '/images/ui/btn_circle.webp',
  btnClose: '/images/ui/btn_close.webp',
  buyScatter: '/images/ui/SS_buy.webp',
  btnStart: '/images/ui/btn_start.webp',
  btnCancel: '/images/ui/btn_cancel.webp',
  txStart: '/images/ui/tx_start.webp',
  txCancel: '/images/ui/tx_cancel.webp',
  iconMenu: '/images/ui/btn_set.webp',
  iconInfo: '/images/ui/btn_info.webp',
  iconWallet: '/images/ui/btn_wallet.webp',
  iconBet: '/images/ui/btn_bet_.webp',
  iconAuto: '/images/ui/btn_auto.webp',
  iconAutoOn: '/images/ui/btn_auto_on.webp',
  iconTurbo: '/images/ui/btn_fast_normal.webp',
  iconTurboOn: '/images/ui/btn_fast_quick.webp',
  iconPlus: '/images/ui/btn_plus.webp',
  iconMinus: '/images/ui/btn_minus.webp',
  // 設定鈕（btn_set）展開的系統選單浮層：9-slice 底圖＋三個 84×84 圖示鈕；
  // 音量為換圖（開 btn_volume ↔ 靜音 btn_volumeNo），非著色。
  menuListBg: '/images/ui/pic_systemLlistBg.webp',
  // 押注鈕（btn_bet_）展開的押注網格浮層（Unity Base/BetListWindow.prefab）：
  // 面板底圖 542×442（底緣中央的三角箭頭是烤進圖裡的，無獨立 sprite）＋格底圖 188×92（未選中／選中換圖）。
  betListBg: '/images/ui/pic_betBg.webp',
  betCellOff: '/images/ui/btn_betChoose.webp',
  betCellOn: '/images/ui/btn_betChoose_on.webp',
  // 自動旋轉設定面板（Unity Base/AutoSpinPanel.prefab）：轉數格底圖 128×66（9-slice 17/20/20/17）
  // ＋選中高亮疊層（同尺寸、Simple）；勾選框 58×58 與勾記 58×58（皆 Simple，勾記以顯隱切換）。
  autoCellBg: '/images/ui/btn_autoBet.webp',
  autoCellOn: '/images/ui/btn_autoBet_on.webp',
  chooseBox: '/images/ui/btn_chooseBg.webp',
  chooseMark: '/images/ui/btn_choosePoint.webp',
  iconHome: '/images/ui/btn_home.webp',
  iconLog: '/images/ui/btn_log.webp',
  iconVolume: '/images/ui/btn_volume.webp',
  iconVolumeOff: '/images/ui/btn_volumeNo.webp',
  // 資訊頁用：空白鍵圖示（操作說明列）與 Wild 說明示意圖。
  keySpace: '/images/ui/btn_space.webp',
  picWildInfo: '/images/ui/pic_load_3.webp',
  barBg: '/images/ui/pic_under_land.webp',
  barBgPortrait: '/images/ui/pic_under_por.webp',
  marqueeBg: '/images/ui/pic_marquee_1.webp',
  winBandBg: '/images/ui/pic_marquee_2.webp',
  infinitas: '/images/ui/infinitas.webp',
  spinButton: '/images/ui/btn_spin_up.webp',
  spinStop: '/images/ui/btn_spin_stop.webp',
  frameTrim: '/images/ui/pic_pillar.webp',
  stageBg: '/images/ui/pic_stageBg.webp',
  stage: '/images/ui/pic_stage.webp',
  // Scatter 累積計量面板（免費遊戲，對照 Unity GamePlayUI 的 Collect 群 FreeSpinIngo）：
  // 面板底圖(884×100) / 獎勵資訊面板(104×56) / 爪子空格與收集格(66×66) / 等級徽章(120×44)。
  collectBg: '/images/ui/pic_collectFGBg.webp',
  collectReward: '/images/ui/pic_collectFG1.webp',
  clawEmpty: '/images/ui/pic_claw_no.webp',
  clawFilled: '/images/ui/pic_claw_up.webp',
  levelBadge1: '/images/ui/tx_LV1.webp',
  levelBadge2: '/images/ui/tx_LV2.webp',
  levelBadge3: '/images/ui/tx_LV3.webp',
  levelBadgeMax: '/images/ui/tx_MAX.webp',
  // 收集面板的「+3 旋轉」「+8 旋轉」本地化字圖（Unity add3Spins/add8Spins 節點；tx_add5Spins 檔名為
  // Unity 命名遺留，圖面內容實為「+8 旋轉」，已逐檔核對）。字圖為 zh-Hant 版（沿 BuyBonus 字圖慣例）。
  add3Spins: '/images/ui/tx_add3Spins.webp',
  add8Spins: '/images/ui/tx_add5Spins.webp',
  // 免費遊戲 SPIN 鈕疊加群組（Unity BottomBar 的 FreeGame 群組）：金繩框紅木底大圓 btn_freeSpin_up
  // ＋「免費旋轉」本地化字圖 tx_freeSpins（zh-Hant）。
  freeSpinDisc: '/images/ui/btn_freeSpin_up.webp',
  freeSpinsLabel: '/images/ui/tx_freeSpins.webp',
  // 「組合方式」本地化字圖（Unity WaysInfo/WaysImage 的 tx_mageways，zh-Hant）。
  waysLabel: '/images/ui/tx_mageways.webp',
  // 免費遊戲進場字卡（Unity FreeSpinsUI 的 Congratulations/YouHaveWonImage/FreeSpinsImage，
  // prefab 預設 sprite GUID 不在來源 repo，runtime 由 S026_AssetTable 換綁 per-locale 圖）。
  txCongrats: '/images/i18n/zh-Hant/tx_congratulations.webp',
  txYouHaveWon: '/images/i18n/zh-Hant/tx_youHaveWon.webp',
  txWinFreeSpins: '/images/i18n/zh-Hant/tx_winFreeSpins.webp',
  // FG 結算卡標題字圖（Unity SettleUI.prefab：CongratulationsImage=大獎(≥10×)／FeatureCompleteImage=
  // 非大獎，同座標 (0,300) 626×90 互斥顯示，副標共用 txYouHaveWon；runtime 由 S026_AssetTable 換綁）。
  txCongrats2: '/images/i18n/zh-Hant/tx_congratulations2.webp',
  txFeatureComplete: '/images/i18n/zh-Hant/tx_featureComplete.webp',
  // FG 進場字卡「收集 Scatter 升級」兩行說明字圖（LandscapeUI 自建 CollectScattersImage 節點：
  // 橫版 (−0.6,−229.3) 984×142、直版 PoriraitUI (0.8,−182.8) scale 0.8）。
  txCollectScatters: '/images/i18n/zh-Hant/tx_collectScatters.webp',
  // 底部列跑馬燈輪播橫幅（Unity BottomBar.prefab 的 Marquee/NormalMask/Image~Image(3)，遮罩 550×100；
  // LandscapeUI/PoriraitUI 覆寫 LocalizeSpriteEvent KeyId 綁 tx_score_1~4，圖寬 1188/1668 恆寬於遮罩故必捲動）。
  txScore1: '/images/i18n/zh-Hant/tx_score_1.webp',
  txScore2: '/images/i18n/zh-Hant/tx_score_2.webp',
  txScore3: '/images/i18n/zh-Hant/tx_score_3.webp',
  txScore4: '/images/i18n/zh-Hant/tx_score_4.webp',
  // 底部列贏分帶的「贏分」標題字圖（Unity BottomBarView.prefab 的 Win/Layout/TextImage，
  // 高 70 × scale 1.15；SlotFramework Art/UI/LocalizeSprite/<locale>/tx_win.png，13 語系齊全）。
  txWin: '/images/i18n/zh-Hant/tx_win.webp',
  // 規則頁免費遊戲倍率示意圖（GameRuleInfo.prefab 的 FreeGameRuleImage_1/Image，280×280 靜態圖）。
  picMultiple: '/images/i18n/zh-Hant/pic_multiple.webp',
} as const

export type UiKey = keyof typeof URLS

/**
 * 多語系字圖對應（Unity S026_AssetTable／Common_AssetTable 的語系切換）：
 * 這些 key 依當前語系改讀 /images/i18n/<locale>/<file>（已複製全部 13 語系來源）。
 * locales 省略＝13 語系齊全；有列＝僅這些語系有檔（其餘回退 en，Unity Localization 的 fallback 慣例）。
 * URLS 內的原路徑（zh-Hant 版複本）保留為最終 fallback。
 */
const LOCALIZED_SPRITES: Partial<Record<UiKey, { readonly file: string; readonly locales?: readonly Locale[] }>> = {
  logo: { file: 'logo.webp' },
  buyBtnLabel: { file: 'tx_btnBuyBonus.webp' },
  txStart: { file: 'tx_start.webp', locales: ['en', 'th', 'zh-Hans', 'zh-Hant'] },
  txCancel: { file: 'tx_cancel.webp', locales: ['en', 'th', 'zh-Hans', 'zh-Hant'] },
  add3Spins: { file: 'tx_add3Spins.webp' },
  add8Spins: { file: 'tx_add5Spins.webp' },
  freeSpinsLabel: { file: 'tx_freeSpins.webp' },
  waysLabel: { file: 'tx_mageways.webp' },
  txCongrats: { file: 'tx_congratulations.webp' },
  txYouHaveWon: { file: 'tx_youHaveWon.webp' },
  txWinFreeSpins: { file: 'tx_winFreeSpins.webp' },
  txCongrats2: { file: 'tx_congratulations2.webp' },
  txFeatureComplete: { file: 'tx_featureComplete.webp' },
  txCollectScatters: { file: 'tx_collectScatters.webp' },
  txScore1: { file: 'tx_score_1.webp' },
  txScore2: { file: 'tx_score_2.webp' },
  txScore3: { file: 'tx_score_3.webp' },
  txScore4: { file: 'tx_score_4.webp' },
  txWin: { file: 'tx_win.webp' },
  picMultiple: { file: 'pic_multiple.webp' },
}

/** 依當前語系解析 key 的實際貼圖 URL（無多語系版本則用 URLS 原路徑）。 */
function urlForKey(key: UiKey): string {
  const localized = LOCALIZED_SPRITES[key]
  if (localized === undefined) return URLS[key]
  const locale = getLocale()
  const effective = localized.locales === undefined || localized.locales.includes(locale) ? locale : 'en'
  return `/images/i18n/${effective}/${localized.file}`
}

/**
 * 購買鈕發光序列幀（Unity BuyBonusLoop.anim：btn_BuyBonus_00000~00058 隔 2 取 30 幀、
 * 60fps 取樣、全長 2.0167s loop；套在 BuyTypeButtons.prefab 的 Anim 節點 140×107 scale 2）。
 */
const BUY_GLOW_URLS: readonly string[] = Array.from(
  { length: 30 },
  (_, i) => `/images/ui/buyglow/btn_BuyBonus_${String(i * 2).padStart(5, '0')}.webp`,
)
/** 發光序列全長（秒，Unity clip m_StopTime）。 */
export const BUY_GLOW_DURATION_S = 2.0166667

let textureCache: ReadonlyMap<UiKey, Texture> | null = null
let buyGlowCache: readonly Texture[] | null = null

export async function loadUiTextures(): Promise<ReadonlyMap<UiKey, Texture>> {
  if (textureCache !== null) return textureCache

  // 先取合圖（ui＝UI 素材＋位圖字型底圖、buyglow＝發光序列 30 幀、i18n-<locale>＝當前語系字圖）；
  // 之後每個 key 都從合圖快取取子貼圖，不再逐檔發請求。合圖缺檔時 loadTexture 會自動退回單張。
  await ensureAtlases('ui', 'buyglow', `i18n-${getLocale()}`)

  const entries = await Promise.all(
    (Object.keys(URLS) as UiKey[]).map(async (key) => {
      // 多語系字圖：語系版載入失敗時回退 URLS 原路徑（zh-Hant 版），不讓單檔缺漏擋整體 UI。
      try {
        const texture = await loadTexture(urlForKey(key))
        return [key, texture] as const
      } catch {
        const texture = await loadTexture(URLS[key])
        return [key, texture] as const
      }
    }),
  )
  textureCache = new Map(entries)
  // 發光序列幀為輔助特效：整批載入失敗不擋 UI（getBuyGlowTextures 回 null 時按鈕退回靜態底圖）。
  try {
    buyGlowCache = await Promise.all(BUY_GLOW_URLS.map((url) => loadTexture(url)))
  } catch {
    buyGlowCache = null
  }
  return textureCache
}

/** 購買鈕發光序列幀（30 張；載入失敗回 null → 呼叫端走靜態 fallback）。 */
export function getBuyGlowTextures(): readonly Texture[] | null {
  return buyGlowCache
}

export function getUiTexture(key: UiKey): Texture {
  if (textureCache === null) {
    throw new Error('UI 貼圖尚未載入，請先呼叫 loadUiTextures()')
  }
  const texture = textureCache.get(key)
  if (texture === undefined) {
    throw new Error(`找不到 UI 貼圖：${key}`)
  }
  return texture
}
